import { ChangeEvent, FormEvent, useEffect, useState } from 'react';

import {
    AutoShipUnitOfMeasure,
    getUomByValue,
    useAutoshipDecisions,
    useCart,
    useOptimizely,
    usePageLayout,
} from '@libs/common-next';
import type {
    AddItemRequestBody,
    FulfillmentGroupsItemUI,
    ItemUI,
    UpdateItemFulfillmentMethodRequestBody,
} from '@libs/common-node';
import { DeliveryType, getCountry } from '@libs/core';

import './SetUpAutoshipButton.scss';

const INITIAL_FREQUENCY = '4_2';

interface ProductInfo {
    id: number;
    isCaseRequiredSTH: boolean | undefined;
    customCaseQuantity: number | null | undefined;
}

export function useQuickAtc(product: ProductInfo) {
    const [isLoading, setIsLoading] = useState(false);
    const { setErrorAlert, setRefreshNeededError } = usePageLayout();
    const { addItem, cart, updateItemFulfillmentMethod } = useCart();
    const { context } = useOptimizely();
    const { isAutoshipCrawl } = useAutoshipDecisions();
    const deliveryMethod = isAutoshipCrawl ? DeliveryType.STH : DeliveryType.AUTOSHIP;
    const country = getCountry();
    const [addedToCart, setAddedToCart] = useState(false);
    const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(false);
    const [selectedFrequency, setSelectedFrequency] = useState(INITIAL_FREQUENCY);
    const frequencyItems = [
        {
            label: '2 weeks',
            value: '2_2',
        },
        {
            label: '3 weeks',
            value: '3_2',
        },
        {
            label: '4 weeks',
            value: '4_2',
        },
        {
            label: '6 weeks',
            value: '6_2',
        },
        {
            label: '8 weeks',
            value: '8_2',
        },
        {
            label: '3 months',
            value: '3_3',
        },
        {
            label: '4 months',
            value: '4_3',
        },
        {
            label: '6 months',
            value: '6_3',
        },
        {
            label: '9 months',
            value: '9_3',
        },
    ];

    const hasCaseRequiredAndQuantity = (selectedProduct: Partial<ProductInfo>) =>
        !!(selectedProduct.isCaseRequiredSTH && selectedProduct.customCaseQuantity);

    useEffect(() => {
        const item = findAutoshipItem(cart?.fulfillmentGroups, product?.id?.toString());
        setAddedToCart(item !== null);
    }, [cart?.fulfillmentGroups, product?.id]);

    const closeDrawer = () => {
        setIsDrawerOpen(false);
    };

    const openDrawer = () => {
        context?.trackEvent('set_up_autoship_click');
        setIsDrawerOpen(true);
    };

    const onFrequencyChange = (event: ChangeEvent<HTMLSelectElement>) => {
        const frequency = event?.target.value;

        if (frequency !== selectedFrequency) {
            setSelectedFrequency(frequency);
        }
    };

    const addToCTCart = async (body: AddItemRequestBody, shouldThrowError?: boolean) => {
        const ctCart = await addItem(body);

        if (ctCart?.id) {
            setAddedToCart(true);
        } else {
            console.error('An error happened when trying to add to cart.');
            setErrorAlert(true);
            if (shouldThrowError) {
                throw new Error();
            }
        }
    };

    const editAutoship = async (item: ItemUI) => {
        await updateItemFulfillmentMethod({
            itemId: item.id,
            fulfillmentMethod: deliveryMethod,
            autoshipFrequency: `${item.autoshipFrequency}`,
            autoshipFrequencyUnitOfMeasure: `${item.autoshipFrequencyUnitOfMeasure}`,
        } as UpdateItemFulfillmentMethodRequestBody);
    };

    const findAutoshipItem = (fulfillmentGroups: FulfillmentGroupsItemUI[] | undefined, product_id: string) => {
        const deliveryMethodText = deliveryMethod == DeliveryType.STH ? 'STH' : 'AUTOSHIP';
        const autoshipGroup = fulfillmentGroups?.find(
            group => group.fulfillmentMethod === (deliveryMethodText as DeliveryType)
        );

        if (!autoshipGroup) return null;

        return autoshipGroup.items.find(i => i.sku === product_id) || null;
    };

    const addToAutoShipProgram = async (ctCardItem: AddItemRequestBody, item: ItemUI) => {
        const autoshipItem = findAutoshipItem(cart?.fulfillmentGroups, product?.id?.toString());
        if (autoshipItem) {
            return editAutoship(item);
        }
        await addToCTCart(ctCardItem, true);
    };

    const minimumCaseQty = hasCaseRequiredAndQuantity(product) ? (product?.customCaseQuantity as number) : 1;
    async function handleFormSubmit(event: FormEvent<HTMLFormElement>) {
        event.preventDefault();
        setIsLoading(true);
        const [amount, measure] = selectedFrequency.split('_');
        const productId = product?.id?.toString();
        const frequencyAmount = parseInt(amount, 10);
        const unitOfMeasure: AutoShipUnitOfMeasure = getUomByValue(parseInt(measure, 10));
        const autoshipItem = findAutoshipItem(cart?.fulfillmentGroups, productId);
        const newItem = {
            id: autoshipItem?.id,
            autoshipFrequency: frequencyAmount,
            autoshipFrequencyUnitOfMeasure: unitOfMeasure,
            quantity: minimumCaseQty,
        } as ItemUI;
        try {
            await addToAutoShipProgram(
                {
                    country,
                    sku: productId,
                    quantity: 1,
                    fulfillmentMethod: deliveryMethod,
                    autoshipFrequency: `${frequencyAmount}`,
                    autoshipFrequencyUnitOfMeasure: `${unitOfMeasure}`,
                },
                newItem
            );
            setAddedToCart(true);
            context?.trackEvent('quick_atc_add_to_cart_click');
            closeDrawer();
        } catch (e) {
            setRefreshNeededError(true);
            console.error(
                `AutoShip: Error while attempting add autoship item to cart. Request parameters:
                    `,
                e
            );
            setErrorAlert(true);
            return;
        } finally {
            setIsLoading(false);
        }
    }

    return {
        handleFormSubmit,
        onFrequencyChange,
        openDrawer,
        closeDrawer,
        frequencyItems,
        isDrawerOpen,
        addedToCart,
        isLoading,
        selectedFrequency,
    };
}
